<template>
  <div
    v-if="!connectionLoading"
    class="api-key-card"
    :style="{ display: isConnectionStatusInactive ? 'block' : 'flex' }"
    :class="{
      'mb-15': $vuetify.display.smAndDown,
      'api-key-card_active': isConnectionStatusActive,
    }"
  >
    <component
      :is="isConnectionStatusPending ? KonstantineApiKey : KonstantineConnection"
      v-bind="componentProps"
      @update-ai-agent-switch="updateAiAgentSwitch"
    >
      <template #icon>
        <div
          class="api-key-card__icon"
          :class="{
            'ai-agent__icon': isConnectionStatusActive || isConnectionStatusInactive || !hasApiKey,
            'mr-5': $vuetify.display.smAndDown && isConnectionStatusInactive,
          }"
          aria-hidden="true"
        >
          <img
            class="logo"
            :src="logo"
          />
        </div>
      </template>

      <template #text>
        <div
          class="api-key-card__text"
          :class="{ 'api-key-card__text_active': isConnectionStatusActive }"
        >
          <div
            :id="titleId"
            class="api-key-card__title"
          >
            <template v-if="isConnectionStatusPending">{{
              $t('dashboard.konstantine.create-api.title')
            }}</template>
            <template v-else-if="isConnectionStatusInactive">
              {{ $t('dashboard.konstantine.create-api.deactivated') }}
            </template>
            <template v-else>
              <span v-if="isConnectionStatusActive">
                {{ $t('dashboard.konstantine.create-api.activated') }}
              </span>
              <span v-else>
                {{ $t('dashboard.konstantine.create-api.activate') }}
              </span>
            </template>
          </div>

          <p
            class="api-key-card__desc"
            v-if="isConnectionStatusPending"
          >
            {{ $t('dashboard.konstantine.create-api.pending') }}
          </p>
          <p
            v-else-if="!isConnectionStatusInactive"
            class="api-key-card__desc"
          >
            <span v-if="isConnectionStatusActive">
              {{ $t('dashboard.konstantine.create-api.subtitle-active') }}
            </span>
            <span v-else>{{ $t('dashboard.konstantine.create-api.subtitle') }}</span>
          </p>
        </div>
      </template>
    </component>
  </div>
</template>

<script setup lang="ts">
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import logo from '@/assets/img/konstantine/logo.png'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import KonstantineApiKey from '@/modules/dashboard/konstantine/KonstantineApiKey.vue'
import KonstantineConnection from '@/modules/dashboard/konstantine/KonstantineConnection.vue'
import GET_CONNECTION from '@/modules/dashboard/konstantine/queries/GetConnection.gql'

import CREATE_CONNECTION from './queries/CreateConnection.gql'
import DEACTIVATE_CONNECTION from './queries/DeactivateConnection.gql'

const { t } = useI18n()
const hasApiKey = computed(() => !!connection.value?.apiKey)
const isConnectionStatusInactive = computed(
  () => !!connection.value?.status && connection.value?.status === 'INACTIVE'
)
const isConnectionStatusPending = computed(
  () => !!connection.value?.status && connection.value?.status === 'PENDING'
)
const isConnectionStatusActive = computed(
  () => !!connection.value?.status && connection.value?.status === 'ACTIVE'
)

const titleId = 'apiKeyTitle-' + Math.random().toString(36).slice(2, 8)

const componentProps = computed(() =>
  hasApiKey.value
    ? { apiKey: connection.value?.apiKey, status: connection.value?.status }
    : { status: connection.value?.status }
)

const {
  result: connectionResult,
  loading: connectionLoading,
  refetch: refetchConnection,
} = useQuery(GET_CONNECTION)

const connection = computed(() => {
  return connectionResult.value?.konstantine?.getConnection ?? null
})

const { mutate: createConnection } = useMutation(CREATE_CONNECTION)
const { mutate: deactivateConnection } = useMutation(DEACTIVATE_CONNECTION)

const isToggling = ref(false)
const updateAiAgentSwitch = async (value: boolean) => {
  if (isToggling.value) return
  try {
    isToggling.value = true
    if (!value && isConnectionStatusActive.value) {
      await deactivateConnection()
      await refetchConnection()
      showSnackbarMessage('success', t('dashboard.konstantine.create-api.deactivated'))
      return
    }

    if (value) {
      await createConnection()
      await refetchConnection()
      showSnackbarMessage('success', t('dashboard.konstantine.create-api.api-key-ready'))
    }
  } catch (error) {
    showSnackbarMessage('error', t('dashboard.konstantine.create-api.error.agent-switch'))
  } finally {
    isToggling.value = false
  }
}
</script>
<style scoped>
.api-key-card {
  position: relative;
  overflow: hidden;
  background-color: white;
  border-radius: 10px;
  padding: 16px;
  gap: 8px;
  display: flex;
  align-items: center;
}

.api-key-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 0;
  background:
    radial-gradient(80% 100% at 40% 60%, #f6d294 0%, rgba(246, 210, 148, 0) 70%),
    radial-gradient(100% 100% at 75% 60%, #20a081 0%, rgba(32, 160, 129, 0) 70%);
  background-repeat: no-repeat;
  background-size: 100% 100%;
  opacity: 0.8;
}

.api-key-card > * {
  position: relative;
  z-index: 1;
}

.api-key-card__icon {
  align-content: center;
  flex: 0 0 auto;
  z-index: 1;
  color: #414142;
}
.ai-agent__icon {
  margin-right: 5%;
}

.api-key-card__text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-self: center;
  z-index: 1;
}

.api-key-card__title {
  font-size: 18px;
  font-style: normal;
  color: #414142;
  font-weight: 700;
  line-height: 26px;
  z-index: 1;
}

.api-key-card__desc {
  margin: 0;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  color: #414142;
  line-height: 18px;
  z-index: 1;
}

@media (min-width: 461px) {
  .api-key-card__text {
    min-width: 200px;
  }
}
@media (max-width: 460px) {
  .api-key-card__text_active {
    max-width: 180px;
  }
  .api-key-card {
    gap: 35px;
  }
  .api-key-card_active {
    gap: 15px;
  }
  .ai-agent__icon {
    margin-right: 0%;
  }
}
</style>
